import React, { useContext, useEffect, useState } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Badge, DotList } from '@motorway/mw-highway-code';
import { VRM } from '@motorway/mw-highway-code/alpha';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { formatPrice } from 'Utilities/formatPrice';
import { VehicleLookupState } from 'Utilities/vehicles/@types';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';
import { Tooltip } from 'Storybook/components/local/Tooltip/Tooltip';
import { TooltipAnchor } from 'Storybook/components/local/Tooltip/TooltipAnchor';

import useTimeToEnquiryExpire from '../../../valuations/Hooks/useTimeToEnquiryExpire';

import { getExpiredBadgeData } from './VehicleCardReturningUser.helpers';
import Texts from './vehicleCardReturningUser.text.json';
import { VehicleCardReturningUserCTA } from './VehicleCardReturningUserCTA';

import styles from './VehicleCardReturningUser.module.scss';

const LocalT = new MDText(Texts);

interface LatestVehicleProps {
  latestVehicle: VehicleLookupState;
}

const ExpiredBadge: React.FC<LatestVehicleProps> = ({ latestVehicle }) => {
  const enquiryDate = latestVehicle.enquiry?.createdAt ?? {};
  const { days } = useTimeToEnquiryExpire({ enquiryDate, vehicle: latestVehicle });

  const { icon, text, variant } = getExpiredBadgeData(days);

  return (
    <div>
      <Badge reverse icon={() => icon} {...{ text, variant }} />
    </div>
  );
};

export const ReturningValuation: React.FC<LatestVehicleProps & { loading: boolean }> = ({ latestVehicle, loading }) => {
  const { enquiry: latestVehicleEnquiry, isLoaded } = useGetVehicleEnquiry({ vehicle: latestVehicle });
  const isLoading = loading || !isLoaded;

  const price = (isLoading)
    ? latestVehicle?.enquiry?.valuation?.estimate
    : latestVehicleEnquiry?.sellerWillAcceptPrice
      ?? latestVehicleEnquiry?.priceHigh
      ?? latestVehicle?.enquiry?.valuation?.estimate;

  return (
    <>
      <span>{formatPrice(price ?? '')}</span>
      <div className={cx(styles.loadingValuation, { [styles.finishedLoading]: !isLoading })} data-testid="loadingValuation">
        <LoadingArrow />
      </div>
    </>
  );
};

export const VehicleCardReturningUser = () => {
  const { vehicleLookupState: latestVehicle } = useContext(VehicleLookupContext);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, []);

  const mileage = LocalT.translate('mileage', { mileage: latestVehicle.enquiry?.mileage?.toLocaleString() });
  const year = latestVehicle.manufactureYear ?? latestVehicle.year;
  const yearFormatted = year ? `${year}` : undefined;

  return (
    <div className={styles.component}>
      <div>
        <div className={styles.badge}>
          <VRM data-thc-vrm size="small" text={latestVehicle.vrm_pretty as string} />
        </div>
        {latestVehicle.enquiry?.valuation?.estimate ? (
          <div className={styles.priceBlock}>
            <div className={styles.price}>
              <ReturningValuation { ...{ latestVehicle, loading }}/>
            </div>
            <Tooltip
              overlay={<div>{LocalT.translate('tooltip')}</div>}
            >
              <span>
                <TooltipAnchor className={styles.tooltipAnchor} filledIcon={false} />
              </span>
            </Tooltip>
          </div>
        ) : null}
        <h5 className={styles.model}>{latestVehicle.make?.display_name} {latestVehicle.model}</h5>
        <DotList
          data-thc-dotlist
          narrow
          data={[
            { content: yearFormatted, key: 'year' },
            { content: latestVehicle.colour, key: 'fuel' },
            { content: mileage, key: 'mileage' },
          ]}
        />
        <ExpiredBadge {...{ latestVehicle }} />
      </div>

      <VehicleCardReturningUserCTA
        buttonLabel={LocalT.translate('labelCTA') as string}
        vrm={latestVehicle.vrm as string}
      />
    </div>
  );
};
